import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  DialogActions
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';

function ContactManagement() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const contactsCollection = collection(db, 'contacts');
      const contactsQuery = query(contactsCollection, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(contactsQuery);
      const contactsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContacts(contactsData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (contact) => {
    setSelectedContact(contact);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContact(null);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#F5F5F5',
      pt: '20px',
      pb: 8,
      ml: { xs: 0, sm: '250px' }
    }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            mb: 4,
            fontWeight: 700,
            color: '#580100'
          }}
        >
          Feedback & Complaints
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: '#580100' }} />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact Info</TableCell>
                  <TableCell>Message Preview</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow 
                    key={contact.id}
                    hover
                    onClick={() => handleOpenDialog(contact)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f8f8f8'
                      }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={contact.type} 
                        size="small"
                        sx={{ 
                          bgcolor: contact.type === 'Feedback' ? '#4caf50' : '#f44336',
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell>{contact.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2">{contact.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2">{contact.phone}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {contact.message}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {contact.timestamp && format(contact.timestamp.toDate(), 'MMM dd, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Contact Detail Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          {selectedContact && (
            <>
              <DialogTitle 
                sx={{ 
                  borderBottom: '1px solid #eee',
                  bgcolor: '#f8f9fa',
                  px: 3,
                  py: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#580100', fontWeight: 600, mb: 0.5 }}>
                      {selectedContact.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedContact.timestamp && 
                        format(selectedContact.timestamp.toDate(), 'MMMM dd, yyyy hh:mm a')}
                    </Typography>
                  </Box>
                  <Chip 
                    label={selectedContact.type} 
                    size="medium"
                    sx={{ 
                      bgcolor: selectedContact.type === 'Feedback' ? '#4caf50' : '#f44336',
                      color: 'white',
                      fontSize: '0.9rem',
                      px: 1
                    }}
                  />
                </Box>
              </DialogTitle>
              <DialogContent sx={{ px: 3, py: 3 }}>
                <Box 
                  sx={{ 
                    mb: 3,
                    p: 2,
                    bgcolor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #eee'
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                      mb: 1
                    }}
                  >
                    Contact Information
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 20, color: '#580100' }} />
                      <Typography variant="body2">
                        {selectedContact.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 20, color: '#580100' }} />
                      <Typography variant="body2">
                        {selectedContact.phone}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                      mb: 1
                    }}
                  >
                    Message
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-line',
                      bgcolor: 'white',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #eee',
                      lineHeight: 1.6
                    }}
                  >
                    {selectedContact.message}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions 
                sx={{ 
                  px: 3, 
                  py: 2,
                  borderTop: '1px solid #eee'
                }}
              >
                <Button 
                  onClick={handleCloseDialog}
                  variant="contained"
                  sx={{ 
                    bgcolor: '#580100',
                    '&:hover': {
                      bgcolor: '#400000'
                    }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
}

export default ContactManagement;
